import { App } from 'antd';
import { Order, OrderItem } from '../types/orders';
import { KdsOrderItemDto } from '../types/kitchen';
import { useAddDailyMenuSelectionsMutation } from '../services/ordersApi';
import { useGetBoolSettingQuery } from '../services/settingsApi';
import { printAgentService } from '../services/printAgentService';
import { bluetoothPrintService } from '../services/bluetoothPrintService';
import { getErrorMessage } from '../utils/errorHelpers';

const canPrint = (item: KdsOrderItemDto): boolean => {
  if (!item.productionCenters || item.productionCenters.length === 0) return true;
  return item.productionCenters.some(pc => pc.printTicket);
};

interface SendArgs {
  order: Order;
  orderItem: OrderItem;
  newSelections: Array<{ courseId: string; optionId: string }>;
  tableLabel: string;
}

/**
 * Sends additional daily-menu courses for an already-comandeed OrderItem and prints a kitchen
 * ticket for the new course rows that route to centers with `printTicket=true`. Mirrors the
 * initial-send print path so cocina/barra still get a paper ticket for incremental adds.
 */
export const useIncrementalDailyMenuSend = () => {
  const { message } = App.useApp();
  const [addDailyMenuSelections, { isLoading }] = useAddDailyMenuSelectionsMutation();
  const { data: printComandasSetting } = useGetBoolSettingQuery('ImprimirComandas');
  const shouldPrintComandas = printComandasSetting?.value ?? true;

  const send = async ({ order, orderItem, newSelections, tableLabel }: SendArgs): Promise<boolean> => {
    if (newSelections.length === 0) {
      message.warning('No se seleccionó ningún tiempo nuevo');
      return false;
    }
    try {
      const result = await addDailyMenuSelections({
        orderId: order.id,
        itemId: orderItem.id,
        newSelections,
      }).unwrap();

      const newItemIds = new Set(result.newKitchenOrderItemIds || []);
      const ko = result.kitchenOrder;
      if (!shouldPrintComandas || !ko || newItemIds.size === 0) {
        message.success('Tiempos enviados a cocina');
        return true;
      }

      const newItems = (ko.items || [])
        .filter(i => newItemIds.has(i.id) && !i.isCancelled)
        .filter(canPrint);

      if (newItems.length === 0) {
        message.success('Tiempos enviados a cocina');
        return true;
      }

      const printItems: OrderItem[] = newItems.map(i => ({
        id: '',
        orderId: order.id,
        productId: '',
        productName: i.productName,
        quantity: i.quantity,
        unitPrice: 0,
        total: 0,
        variants: i.variants
          ? [{ id: '', orderItemId: '', variantId: '', variantName: i.variants, priceModifier: 0 }]
          : [],
        notes: i.notes || undefined,
        isCancelled: false,
      }));

      const printAgentAvailable = await printAgentService.isAvailable();
      const bluetoothAvailable = bluetoothPrintService.isAvailable();
      let printed = false;
      let printAttempted = false;

      if (printAgentAvailable) {
        printAttempted = true;
        const r = await printAgentService.printKitchenOrder(
          order, tableLabel, order.assignedWaiterName || '', printItems
        );
        printed = r.success;
        if (!r.success) console.warn('Print Agent error (incremental):', r.error);
      } else if (bluetoothAvailable) {
        printAttempted = true;
        const r = await bluetoothPrintService.printKitchenOrder(
          order, tableLabel, order.assignedWaiterName || '', printItems
        );
        printed = r.success;
        if (!r.success) console.warn('Bluetooth error (incremental):', r.error);
      }

      if (printed) {
        message.success(`Tiempos enviados a cocina, ${printItems.length} impreso(s)`);
      } else if (printAttempted) {
        // Kitchen got the data, but the printer didn't — surface this so the waiter knows
        // to reprint manually instead of assuming the ticket is in the kitchen.
        message.warning('Tiempos enviados a cocina, pero la impresora fallo. Reimprime manualmente.');
      } else {
        message.success('Tiempos enviados a cocina');
      }
      return true;
    } catch (err) {
      message.error(getErrorMessage(err) || 'No se pudieron agregar los tiempos');
      return false;
    }
  };

  return { send, isLoading };
};
